import { catchError, concatMap, map, take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, Observer, of } from 'rxjs';
import { ErrorCode } from '../origami-api/constants';
import { SiteService, SiteServiceModel } from '../origami-api/services/site.service';
import { DialogService } from './dialogs/dialog.service';
import { ConfidentialityAgreementComponent } from '@app/shared/misc/confidentiality-agreement/confidentiality-agreement.component';
import { ErrorsInformationComponent } from './dialogs/components/errors-information/errors-information.component';
import { NotificationsService } from './notifications/notifications.service';
import { AppLoadingService } from '@app/services/app-loading.service';
import { LocalStorageService } from '@app/services/local-storage.service';

export interface LoginToSiteInfo {
  id: number;
  error: ErrorCode;
  details: SiteServiceModel.LoginToSiteResult;
}

// for old UI to indicate that the CA is accepted
const ROOM_ACCEPTED_CA_ID = 'ROOM_ACCEPTED_CA_ID';

@Injectable()
export class LoginToRoomService {
  private roomInfo: LoginToSiteInfo;
  readonly strings = tiStrings.tiComponents.room.components.roomProfileWizard;

  constructor(
    private siteService: SiteService,
    public dialogService: DialogService,
    public notificationService: NotificationsService,
    private readonly loadingService: AppLoadingService,
    private readonly localStorageService: LocalStorageService,
  ) {}

  loginWithConfidentialityAgreementId(
    roomId: number,
    confidentialityAgreementId?: number,
    superAdmin?: boolean,
  ): Observable<LoginToSiteInfo> {
    return this.login(roomId, true, confidentialityAgreementId).pipe(
      concatMap((res) => {
        if (res.error === ErrorCode.CA_ACCEPT_REQUIRED) {
          this.loadingService.hideLoading();
          return new Observable((obs: Observer<LoginToSiteInfo>) => {
            this.dialogService.showDialog(ConfidentialityAgreementComponent, (instance, close) => {
              instance.roomId = roomId;
              instance.close.pipe(take(1)).subscribe((caId) => {
                close(caId);

                if (caId) {
                  return this.loginWithConfidentialityAgreementId(roomId, caId)
                    .pipe(take(1))
                    .subscribe((roomInfo) => obs.next(roomInfo));
                }
                obs.next(res);
              });
            });
          });
        }

        if (res.error === ErrorCode.ROOM_CREATED_WITH_ISSUES && superAdmin) {
          return new Observable((obs: Observer<LoginToSiteInfo>) => {
            const errors: any = res.details.CreateSiteResult?.SiteCreationResult;
            this.loadingService.hideLoading();
            this.dialogService.showDialog(ErrorsInformationComponent, (instance, close) => {
              instance.data = errors;
              instance.treeDataText = this.strings.roomCreationErrors;
              instance.roomId = roomId;
              instance.close.pipe(take(1)).subscribe(() => {
                close(res);

                obs.next(res);
              });

              instance.reset
                .pipe(
                  concatMap(() => {
                    instance.isLoading = true;
                    return this.siteService.resetSiteCreationStatus({ siteId: roomId });
                  }),
                )
                .subscribe((response) => {
                  instance.isLoading = false;
                  if (response.Result) {
                    close(res);
                    this.notificationService.showSuccess(this.strings.errorsReset);
                    obs.next(res);
                  }
                });
            });
          });
        }

        if (res.error === ErrorCode.ROOM_CREATED_WITH_ISSUES && !superAdmin) {
          this.notificationService.showWarning(this.strings.roomCreatedWithErrors);
        }

        if (res.details && res.details.IsMissingTIAutomateLibrary && superAdmin) {
          this.notificationService.showWarning(this.strings.missingTIAutomateLibrary, null, 6000);
        }

        return of(res);
      }),
    );
  }

  login(roomId: number, force = false, confidentialityAgreementId?: number): Observable<LoginToSiteInfo> {
    if (this.roomInfo && this.roomInfo.details && !force && this.roomInfo.id === roomId) {
      return of(this.roomInfo);
    }

    window[ROOM_ACCEPTED_CA_ID] = confidentialityAgreementId;
    this.siteService.dropAllCache();

    return this.siteService
      .loginToSite(
        {
          siteId: roomId,
          agreementInfo: confidentialityAgreementId
            ? {
                AgreementId: confidentialityAgreementId,
                Accepted: true,
              }
            : null,
        },
        [ErrorCode.CA_ACCEPT_REQUIRED, ErrorCode.ROOM_CREATED_WITH_ISSUES],
      )
      .pipe(
        map((res) => ({ id: roomId, error: res.ErrorCode, details: res.Result })),
        catchError((res) => of({ id: roomId, error: res.ErrorCode, details: null })),
        tap((res) => (this.roomInfo = res)),
      );
  }

  logout() {
    this.roomInfo = null;
    window[ROOM_ACCEPTED_CA_ID] = null;
  }
}
